// @ts-nocheck
// Three.js type compatibility issues between versions - runtime works correctly
import { useEffect, useRef, useState, useCallback } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js';
import { ParameterHotspots, ParameterWithPosition } from '../../../components/ParameterHotspots';

interface ScenePanelProps {
  status: string;
  onExpandToggle: () => void;
  isExpanded: boolean;
  parameters?: ParameterWithPosition[];
  onParameterChange?: (name: string, value: number | string | boolean) => void;
  onParameterApply?: (name: string, value: number | string | boolean) => void;
  showHotspots?: boolean;
}

export const ScenePanel: React.FC<ScenePanelProps> = ({ 
  status, 
  onExpandToggle, 
  isExpanded,
  parameters = [],
  onParameterChange,
  onParameterApply,
  showHotspots = true,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const arrowCanvasRef = useRef<HTMLCanvasElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const arrowRendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const persCameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const orthCameraRef = useRef<THREE.OrthographicCamera | null>(null);
  const persControlsRef = useRef<OrbitControls | null>(null);
  const orthControlsRef = useRef<OrbitControls | null>(null);
  const cameraTypeRef = useRef<'Perspective' | 'Orthographic'>('Orthographic');
  const objectsRef = useRef<any[]>([]);
  const raycasterObjRef = useRef<THREE.Mesh[]>([]);
  const currentModelRef = useRef<THREE.Group | null>(null);
  const renderRequestedRef = useRef(false);
  const animationFrameRef = useRef<number | null>(null);
  const isDraggingArrowRef = useRef(false);
  const dragStartPosRef = useRef({ x: 0, y: 0 });
  const objectOffsetRef = useRef(new THREE.Vector3(0, 0, 0));
  
  // State for parameter hotspots
  const [currentCamera, setCurrentCamera] = useState<THREE.Camera | null>(null);
  const [modelBounds, setModelBounds] = useState<THREE.Box3 | null>(null);

  // Update model bounds when model changes
  const updateModelBounds = useCallback(() => {
    if (currentModelRef.current) {
      const bounds = new THREE.Box3().setFromObject(currentModelRef.current);
      setModelBounds(bounds);
    }
  }, []);

  // Handler for parameter changes (live preview)
  const handleParameterChange = useCallback((name: string, value: number | string | boolean) => {
    if (onParameterChange) {
      onParameterChange(name, value);
    }
  }, [onParameterChange]);

  // Handler for applying parameter changes
  const handleParameterApply = useCallback((name: string, value: number | string | boolean) => {
    if (onParameterApply) {
      onParameterApply(name, value);
    }
  }, [onParameterApply]);

  useEffect(() => {
    if (!canvasRef.current || !arrowCanvasRef.current) return;

    THREE.Object3D.DEFAULT_UP = new THREE.Vector3(0, 0, 1);

    const canvas = canvasRef.current;
    const arrowCanvas = arrowCanvasRef.current;

    // Main scene
    const scene = new THREE.Scene();
    sceneRef.current = scene;

    const renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: true,
      canvas: canvas
    });
    renderer.setClearColor(0x000000, 0);
    rendererRef.current = renderer;

    // Lighting
    scene.add(new THREE.HemisphereLight(0xC7E8FF, 0xFFE3B3, 0.4));
    const dLight1 = new THREE.DirectionalLight(0xffffff, 0.4);
    dLight1.position.set(5, -2, 3);
    scene.add(dLight1);
    const dLight2 = new THREE.DirectionalLight(0xffffff, 0.4);
    dLight2.position.set(-5, 2, 3);
    scene.add(dLight2);

    // Cameras
    const aspectRatio = canvas.clientWidth / canvas.clientHeight;
    const viewSize = 300;

    const persCamera = new THREE.PerspectiveCamera(50, aspectRatio, 1, 100000);
    persCamera.position.set(100, 100, 100);
    persCameraRef.current = persCamera;

    const orthCamera = new THREE.OrthographicCamera(
      -aspectRatio * viewSize / 2, aspectRatio * viewSize / 2,
      viewSize / 2, -viewSize / 2, -100000, 100000);
    orthCamera.position.set(100, 100, 100);
    orthCameraRef.current = orthCamera;

    // Controls
    const persControls = new OrbitControls(persCamera, renderer.domElement);
    persControls.target = new THREE.Vector3(0, 0, 0);
    persControls.update();
    persControlsRef.current = persControls;

    const orthControls = new OrbitControls(orthCamera, renderer.domElement);
    orthControls.target = new THREE.Vector3(0, 0, 0);
    orthControls.update();
    orthControlsRef.current = orthControls;

    // Arrow helper
    const arrowRenderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: true,
      canvas: arrowCanvas
    });
    arrowRenderer.setClearColor(0x000000, 0);
    
    // Initialize with canvas size (should be 150x150 from CSS)
    const arrowPixelRatio = window.devicePixelRatio;
    const arrowWidth = arrowCanvas.clientWidth || 150;
    const arrowHeight = arrowCanvas.clientHeight || 150;
    arrowRenderer.setSize(arrowWidth * arrowPixelRatio, arrowHeight * arrowPixelRatio, false);
    arrowRendererRef.current = arrowRenderer;

    const arrowScene = new THREE.Scene();
    const arrowCamera = new THREE.PerspectiveCamera(
      50, arrowWidth / arrowHeight, 1, 500);
    arrowCamera.up = persCamera.up;

    const arrowPos = new THREE.Vector3(0, 0, 0);
    arrowScene.add(new THREE.ArrowHelper(
      new THREE.Vector3(1, 0, 0), arrowPos, 60, 0x7F2020, 20, 10));
    arrowScene.add(new THREE.ArrowHelper(
      new THREE.Vector3(0, 1, 0), arrowPos, 60, 0x207F20, 20, 10));
    arrowScene.add(new THREE.ArrowHelper(
      new THREE.Vector3(0, 0, 1), arrowPos, 60, 0x20207F, 20, 10));
    arrowScene.add(new THREE.Mesh(
      new THREE.BoxGeometry(40, 40, 40),
      new THREE.MeshLambertMaterial({ color: 0xaaaaaa, flatShading: false })
    ));
    arrowScene.add(new THREE.HemisphereLight(0xC7E8FF, 0xFFE3B3, 1.2));

    (window as any).arrowScene = arrowScene;
    (window as any).arrowCamera = arrowCamera;
    (window as any).arrowRenderer = arrowRenderer;

    // Render function
    const render = () => {
      renderRequestedRef.current = false;
      
      if (persControlsRef.current) persControlsRef.current.update();
      if (orthControlsRef.current) orthControlsRef.current.update();
      
      // Make arrow camera follow the main camera rotation
      const camera = cameraTypeRef.current === 'Perspective' ? persCamera : orthCamera;
      const controls = cameraTypeRef.current === 'Perspective' ? persControls : orthControls;
      
      // Render arrow canvas - always render if initialized
      const arrowScene = (window as any).arrowScene;
      const arrowCamera = (window as any).arrowCamera;
      const arrowRenderer = arrowRendererRef.current;
      
      if (arrowScene && arrowCamera && arrowRenderer && arrowCanvas.clientWidth > 0) {
        // Calculate the direction from camera to target
        const direction = new THREE.Vector3();
        direction.subVectors(controls.target, camera.position).normalize();
        
        // Position arrow camera at a fixed distance, looking at origin
        arrowCamera.position.copy(direction.multiplyScalar(-200));
        arrowCamera.lookAt(arrowScene.position);
        arrowCamera.up.copy(camera.up);
        
        // Ensure renderer size matches canvas
        const pixelRatio = window.devicePixelRatio;
        const arrowWidth = arrowCanvas.clientWidth * pixelRatio | 0;
        const arrowHeight = arrowCanvas.clientHeight * pixelRatio | 0;
        if (arrowWidth > 0 && arrowHeight > 0) {
          arrowRenderer.setSize(arrowWidth, arrowHeight, false);
          arrowCamera.aspect = arrowCanvas.clientWidth / arrowCanvas.clientHeight;
          arrowCamera.updateProjectionMatrix();
        }
        
        arrowRenderer.render(arrowScene, arrowCamera);
      }

      if (cameraTypeRef.current === 'Perspective') {
        renderer.render(scene, persCamera);
      } else {
        renderer.render(scene, orthCamera);
      }
    };

    const requestRender = () => {
      if (!renderRequestedRef.current) {
        renderRequestedRef.current = true;
        animationFrameRef.current = requestAnimationFrame(render);
      }
    };

    // Event listeners
    persControls.addEventListener('change', requestRender);
    orthControls.addEventListener('change', requestRender);
    
    const onMouseMove = (e: MouseEvent) => {
      // Get canvas bounding rect to calculate correct mouse coordinates
      const rect = canvas.getBoundingClientRect();
      const mouseX = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const mouseY = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      
      const camera = cameraTypeRef.current === 'Perspective' ? persCamera : orthCamera;
      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(new THREE.Vector2(mouseX, mouseY), camera);
      const intersects = raycaster.intersectObjects(raycasterObjRef.current);

      let chosen = '';
      for (const i of intersects) {
        const m = (i.object as THREE.Mesh).material as THREE.MeshLambertMaterial;
        if (m.opacity > 0) {
          if (m.emissive.getHex() == 0x000000) {
            m.emissive.setHex(0x777777);
            m.needsUpdate = true;
            requestRender();
          }
          chosen = i.object.name;
          break;
        }
      }
      
      for (const r of raycasterObjRef.current) {
        if (r.name == chosen) {
          continue;
        }
        const mat = r.material as THREE.MeshLambertMaterial;
        if (mat.emissive.getHex() != 0x000000) {
          mat.emissive.setHex(0x000000);
          mat.needsUpdate = true;
          requestRender();
        }
      }
    };

    canvas.addEventListener('mousemove', onMouseMove);

    // Arrow canvas drag handlers - rotate camera view when dragging
    const onArrowMouseDown = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      isDraggingArrowRef.current = true;
      dragStartPosRef.current = { x: e.clientX, y: e.clientY };
      arrowCanvas.style.cursor = 'grabbing';
    };

    const onArrowMouseMove = (e: MouseEvent) => {
      if (!isDraggingArrowRef.current) return;
      
      const deltaX = e.clientX - dragStartPosRef.current.x;
      const deltaY = e.clientY - dragStartPosRef.current.y;
      
      // Get current camera and controls
      const camera = cameraTypeRef.current === 'Perspective' ? persCamera : orthCamera;
      const controls = cameraTypeRef.current === 'Perspective' ? persControls : orthControls;
      
      // Sensitivity for rotation (radians per pixel)
      const sensitivity = 0.01;
      
      // Calculate spherical coordinates from current camera position
      const offset = new THREE.Vector3().subVectors(camera.position, controls.target);
      const radius = offset.length();
      
      // Convert to spherical coordinates
      let theta = Math.atan2(offset.x, offset.y); // Azimuthal angle (around Z)
      let phi = Math.acos(Math.max(-1, Math.min(1, offset.z / radius))); // Polar angle from Z
      
      // Apply rotation from mouse movement
      theta -= deltaX * sensitivity; // Horizontal drag rotates around Z
      phi -= deltaY * sensitivity;   // Vertical drag changes elevation
      
      // Clamp phi to avoid flipping (keep between 0.1 and PI - 0.1)
      phi = Math.max(0.1, Math.min(Math.PI - 0.1, phi));
      
      // Convert back to Cartesian coordinates
      const sinPhi = Math.sin(phi);
      offset.x = radius * sinPhi * Math.sin(theta);
      offset.y = radius * sinPhi * Math.cos(theta);
      offset.z = radius * Math.cos(phi);
      
      // Update camera position
      camera.position.copy(controls.target).add(offset);
      camera.lookAt(controls.target);
      
      // Update controls
      controls.update();
      
      dragStartPosRef.current = { x: e.clientX, y: e.clientY };
      requestRender();
    };

    const onArrowMouseUp = () => {
      if (isDraggingArrowRef.current) {
        isDraggingArrowRef.current = false;
        arrowCanvas.style.cursor = 'grab';
      }
    };

    const onArrowMouseLeave = () => {
      if (isDraggingArrowRef.current) {
        isDraggingArrowRef.current = false;
        arrowCanvas.style.cursor = 'grab';
      }
    };

    // Click handler for snapping to standard views
    const onArrowClick = (e: MouseEvent) => {
      // Only trigger if it was a quick click (not a drag)
      const deltaX = Math.abs(e.clientX - dragStartPosRef.current.x);
      const deltaY = Math.abs(e.clientY - dragStartPosRef.current.y);
      if (deltaX > 5 || deltaY > 5) return; // Was a drag, not a click
      
      const rect = arrowCanvas.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      
      const arrowScene = (window as any).arrowScene;
      const arrowCamera = (window as any).arrowCamera;
      if (!arrowScene || !arrowCamera) return;
      
      // Raycast to find which arrow was clicked
      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(new THREE.Vector2(x, y), arrowCamera);
      const intersects = raycaster.intersectObjects(arrowScene.children, true);
      
      if (intersects.length === 0) return;
      
      // Get the clicked object and determine view direction
      const clickedObj = intersects[0].object;
      let viewDirection: THREE.Vector3 | null = null;
      
      // Check parent for ArrowHelper (arrows have Line and Cone children)
      const parent = clickedObj.parent;
      if (parent instanceof THREE.ArrowHelper) {
        // Get the arrow direction
        const dir = parent.getWorldDirection(new THREE.Vector3());
        // Camera should look FROM the opposite direction
        viewDirection = dir.multiplyScalar(-1);
      } else if (clickedObj instanceof THREE.Mesh && clickedObj.geometry instanceof THREE.BoxGeometry) {
        // Clicked on the center cube - go to isometric view
        viewDirection = new THREE.Vector3(1, 1, 1).normalize().multiplyScalar(-1);
      }
      
      if (viewDirection) {
        // Animate camera to new position
        const camera = cameraTypeRef.current === 'Perspective' ? persCamera : orthCamera;
        const controls = cameraTypeRef.current === 'Perspective' ? persControls : orthControls;
        const distance = camera.position.distanceTo(controls.target);
        
        // Calculate new camera position
        const newPosition = new THREE.Vector3()
          .copy(controls.target)
          .add(viewDirection.multiplyScalar(-distance));
        
        // Animate smoothly
        const startPos = camera.position.clone();
        const startTime = performance.now();
        const duration = 300; // ms
        
        const animateToView = () => {
          const elapsed = performance.now() - startTime;
          const t = Math.min(elapsed / duration, 1);
          // Ease out cubic
          const eased = 1 - Math.pow(1 - t, 3);
          
          camera.position.lerpVectors(startPos, newPosition, eased);
          camera.lookAt(controls.target);
          controls.update();
          requestRender();
          
          if (t < 1) {
            requestAnimationFrame(animateToView);
          }
        };
        
        animateToView();
      }
    };

    arrowCanvas.addEventListener('mousedown', onArrowMouseDown);
    arrowCanvas.addEventListener('click', onArrowClick);
    window.addEventListener('mousemove', onArrowMouseMove);
    window.addEventListener('mouseup', onArrowMouseUp);
    arrowCanvas.addEventListener('mouseleave', onArrowMouseLeave);

    const onResize = () => {
      const pixelRatio = window.devicePixelRatio;
      const width = canvas.clientWidth * pixelRatio | 0;
      const height = canvas.clientHeight * pixelRatio | 0;
      const needResize = canvas.width !== width || canvas.height !== height;
      const aspect = canvas.clientWidth / canvas.clientHeight;
      
      if (needResize) {
        renderer.setSize(width, height, false);

        const viewSize = 300;
        const change = 1;
        const newSize = viewSize * change;
        orthCamera.left = -aspect * newSize / 2;
        orthCamera.right = aspect * newSize / 2;
        orthCamera.top = newSize / 2;
        orthCamera.bottom = -newSize / 2;
        orthCamera.updateProjectionMatrix();

        persCamera.aspect = canvas.clientWidth / canvas.clientHeight;
        persCamera.updateProjectionMatrix();
      }

      // Resize arrow canvas
      const arrowRenderer = arrowRendererRef.current;
      if (arrowCanvas && arrowRenderer && arrowCanvas.clientWidth > 0 && arrowCanvas.clientHeight > 0) {
        const arrowWidth = arrowCanvas.clientWidth * pixelRatio | 0;
        const arrowHeight = arrowCanvas.clientHeight * pixelRatio | 0;
        arrowRenderer.setSize(arrowWidth, arrowHeight, false);
        const arrowCamera = (window as any).arrowCamera;
        if (arrowCamera) {
          arrowCamera.aspect = arrowCanvas.clientWidth / arrowCanvas.clientHeight;
          arrowCamera.updateProjectionMatrix();
        }
      }

      requestRender();
    };

    window.addEventListener('resize', onResize);
    onResize();
    
    // Initial render of arrow canvas - ensure it's visible
    const initialArrowRender = () => {
      const arrowScene = (window as any).arrowScene;
      const arrowCamera = (window as any).arrowCamera;
      const arrowRenderer = arrowRendererRef.current;
      if (arrowScene && arrowCamera && arrowRenderer && arrowCanvas.clientWidth > 0) {
        // Initial position - look at origin from a distance
        arrowCamera.position.set(0, 0, 200);
        arrowCamera.lookAt(arrowScene.position);
        arrowCamera.up.copy(persCamera.up);
        arrowRenderer.render(arrowScene, arrowCamera);
      } else if (arrowCanvas.clientWidth === 0) {
        // Canvas not ready yet, try again
        setTimeout(initialArrowRender, 50);
      }
    };
    
    // Try initial render after a short delay to ensure canvas is ready
    setTimeout(initialArrowRender, 100);
    requestRender();
    
    // Set initial camera state for hotspots
    setCurrentCamera(cameraTypeRef.current === 'Perspective' ? persCamera : orthCamera);

    // Expose functions globally
    window.clearScene = () => {
      try {
        if (currentModelRef.current) {
          scene.remove(currentModelRef.current);
          currentModelRef.current = null;
        }
        raycasterObjRef.current = [];
        objectsRef.current = [];
        objectOffsetRef.current.set(0, 0, 0); // Reset offset when clearing
        (window as any).webglData = null;
        setModelBounds(null); // Clear bounds for hotspots
        requestRender();
      } catch (_) {}
    };

    window.loadWebGLData = () => {
      const dataToUse = (window as any).webglData;
      
      if (!dataToUse || !dataToUse.objects) {
        return;
      }

      if (currentModelRef.current) {
        scene.remove(currentModelRef.current);
        currentModelRef.current = null;
      }

      if (dataToUse.compressed) {
        // Compressed format not yet supported
        return;
      }

      createMeshes(dataToUse);
    };

    // Load STL from blob (for OpenSCAD WASM output)
    window.loadSTLBlob = async (blob: Blob) => {
      if (currentModelRef.current) {
        scene.remove(currentModelRef.current);
        currentModelRef.current = null;
      }

      try {
        const arrayBuffer = await blob.arrayBuffer();
        const loader = new STLLoader();
        const geometry = loader.parse(arrayBuffer);
        geometry.center();
        geometry.computeVertexNormals();

        const currentModel = new THREE.Group();
        currentModelRef.current = currentModel;

        // Create mesh with a clean white material
        const material = new THREE.MeshStandardMaterial({
          color: '#F5F5F5',
          metalness: 0.3,
          roughness: 0.5,
          side: THREE.DoubleSide,
        });

        const mesh = new THREE.Mesh(geometry, material);
        mesh.name = 'OpenSCAD_Model';
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        // Rotate to match OpenSCAD coordinate system (Z-up to Y-up for Three.js)
        mesh.rotation.x = -Math.PI / 2;
        raycasterObjRef.current = [mesh];

        currentModel.add(mesh);
        centerAndScaleModel(currentModel);
        currentModel.position.copy(objectOffsetRef.current);
        scene.add(currentModel);
        requestRender();
        
        // Update bounds for parameter hotspots
        updateModelBounds();
      } catch (error) {
        // STL loading error - silent fail, status already shows error
      }
    };

    const createMeshes = (data: any) => {
      objectsRef.current = [];
      const currentModel = new THREE.Group();
      currentModelRef.current = currentModel;
      
      for (const obj of data.objects) {
        if (!obj.vertices || !obj.facets || obj.vertices.length === 0 || obj.facets.length === 0) {
          // Skip objects with no valid geometry
          continue;
        }

        const positions = new Float32Array(obj.facets.length * 9);
        
        for (let i = 0; i < obj.facets.length; i += 3) {
          const faceIndex = i / 3;
          const v1Index = obj.facets[i] * 3;
          const v2Index = obj.facets[i + 1] * 3;
          const v3Index = obj.facets[i + 2] * 3;
          
          positions[faceIndex * 9 + 0] = obj.vertices[v1Index];
          positions[faceIndex * 9 + 1] = obj.vertices[v1Index + 1];
          positions[faceIndex * 9 + 2] = obj.vertices[v1Index + 2];
          
          positions[faceIndex * 9 + 3] = obj.vertices[v2Index];
          positions[faceIndex * 9 + 4] = obj.vertices[v2Index + 1];
          positions[faceIndex * 9 + 5] = obj.vertices[v2Index + 2];
          
          positions[faceIndex * 9 + 6] = obj.vertices[v3Index];
          positions[faceIndex * 9 + 7] = obj.vertices[v3Index + 1];
          positions[faceIndex * 9 + 8] = obj.vertices[v3Index + 2];
        }
        
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        geometry.computeVertexNormals();
        
        const material = new THREE.MeshLambertMaterial({
          color: obj.color || '#727980',
          side: THREE.DoubleSide,
          opacity: obj.opacity || 1.0,
          transparent: (obj.opacity || 1.0) != 1.0
        });
        
        const mesh = new THREE.Mesh(geometry, material);
        mesh.name = obj.name;
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        raycasterObjRef.current.push(mesh);
        
        currentModel.add(mesh);
        
        objectsRef.current.push({
          data: obj,
          mesh: mesh
        });
      }
      
      centerAndScaleModel(currentModel);
      // Apply any existing offset
      currentModel.position.copy(objectOffsetRef.current);
      scene.add(currentModel);
      requestRender();
      
      // Update bounds for parameter hotspots
      updateModelBounds();
      
      setTimeout(() => {
        render();
      }, 100);
    };

    const centerAndScaleModel = (model: THREE.Group) => {
      const box = new THREE.Box3().setFromObject(model);
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z);
      const scale = maxDim > 0 ? 100 / maxDim : 1;
      
      model.scale.setScalar(scale);
      model.position.sub(center.multiplyScalar(scale));
    };

    return () => {
      window.removeEventListener('resize', onResize);
      canvas.removeEventListener('mousemove', onMouseMove);
      arrowCanvas.removeEventListener('mousedown', onArrowMouseDown);
      arrowCanvas.removeEventListener('click', onArrowClick);
      window.removeEventListener('mousemove', onArrowMouseMove);
      window.removeEventListener('mouseup', onArrowMouseUp);
      arrowCanvas.removeEventListener('mouseleave', onArrowMouseLeave);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (persControlsRef.current) {
        persControlsRef.current.dispose();
      }
      if (orthControlsRef.current) {
        orthControlsRef.current.dispose();
      }
      if (rendererRef.current) {
        rendererRef.current.dispose();
      }
      if (arrowRendererRef.current) {
        arrowRendererRef.current.dispose();
      }
    };
  }, []);

  return (
    <div className="scene-panel">
      <div className={`canvas-container ${isExpanded ? 'expanded' : ''}`} id="canvasContainer">
        <button 
          className="expand-canvas-button" 
          onClick={onExpandToggle}
          title="Expand canvas"
        >
          <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 2L2 6M2 6H6M2 6V2M10 2L14 6M14 6H10M14 6V2M6 14L2 10M2 10H6M2 10V14M10 14L14 10M14 10H10M14 10V14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <canvas ref={canvasRef} id="mainCanvas"></canvas>
        <canvas ref={arrowCanvasRef} id="arrowCanvas"></canvas>
        
        {/* Parameter Hotspots Overlay */}
        {showHotspots && parameters.length > 0 && (
          <ParameterHotspots
            parameters={parameters}
            canvasRef={canvasRef}
            camera={currentCamera}
            modelBounds={modelBounds}
            onParameterChange={handleParameterChange}
            onApplyChange={handleParameterApply}
            isVisible={showHotspots}
          />
        )}
        
        {status && <div className="status" id="status">{status}</div>}
      </div>
    </div>
  );
};

